import Button from '@orn/shared-web/components/Button';

import { cadenceFrequency, sameCadence, type IBillingCadence } from '../../../lib/billing/billingCadence';
import { SettingsCard } from '../common';

import { formatPeriodEnd, summarizeBilling } from './billingSummary';

import type { SubscriptionStatus } from '../../../__generated__/globalTypes';
import type * as React from 'react';

export interface IBillingSubscription {
  status: SubscriptionStatus;
  isEntitled: boolean;
  /** What the membership debits on, read from its price. */
  cadence: IBillingCadence;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
  /**
   * A scheduled change and when it lands. The date can stand without the
   * cadence, so the line names the date alone rather than disappearing.
   */
  pendingCadence: IBillingCadence | null;
  pendingIntervalAt: Date | null;
  paymentMethod: { institutionName: string | null; mask: string | null } | null;
}

export interface IBillingSettingsBaseProps {
  /** Null when the household has never subscribed. */
  subscription: IBillingSubscription | null;
  /**
   * Opens checkout. Absent while the billing gate is closed, which is what
   * keeps the entry point from appearing before checkout exists.
   */
  onSubscribe?: () => void;
  /**
   * Portal entry points shown to an entitled subscriber. Supplied by the
   * container so this component stays presentational.
   */
  manageMembership?: React.ReactNode;
  /**
   * Opens the payment-account switch. Offered only to an entitled subscriber;
   * absent while the billing gate is closed.
   */
  onSwitchAccount?: () => void;
}

/**
 * The "Billing" settings tab.
 *
 * Renders a lapsed subscription as well as a live one — a household that
 * canceled should read "canceled", not the never-subscribed state, so the
 * card distinguishes "no subscription yet" from "no longer subscribed".
 */
const BillingSettingsBase = ({
  subscription,
  onSubscribe,
  manageMembership,
  onSwitchAccount,
}: IBillingSettingsBaseProps) => (
  <div className="flex max-w-[736px] flex-col gap-5">
    <SettingsCard title="Plan & billing" subtitle="Your subscription and payment details.">
      {subscription === null ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <p className="text-quaternary text-xs font-semibold uppercase tracking-wide">Current plan</p>
            <p className="text-primary text-md font-medium">No subscription</p>
            <p className="text-tertiary text-sm">
              You don't have a subscription yet. Start one to keep using Mantle after your trial.
            </p>
          </div>
          {onSubscribe !== undefined && (
            <div>
              <Button size="md" onClick={onSubscribe}>
                Start subscription
              </Button>
            </div>
          )}
        </div>
      ) : (
        <BillingDetail
          subscription={subscription}
          onSubscribe={onSubscribe}
          manageMembership={manageMembership}
          onSwitchAccount={onSwitchAccount}
        />
      )}
    </SettingsCard>
  </div>
);

interface IBillingDetailProps {
  subscription: IBillingSubscription;
  onSubscribe?: () => void;
  manageMembership?: React.ReactNode;
  onSwitchAccount?: () => void;
}

const BillingDetail = ({ subscription, onSubscribe, manageMembership, onSwitchAccount }: IBillingDetailProps) => {
  const { planLabel, detail, periodEndMeaning } = summarizeBilling(subscription.status, subscription.cancelAtPeriodEnd);
  const { paymentMethod, currentPeriodEnd } = subscription;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="text-quaternary text-xs font-semibold uppercase tracking-wide">Current plan</p>
        <p className="text-primary text-md font-medium">{planLabel}</p>
        <p className="text-tertiary text-sm">{detail}</p>
      </div>

      <div className="flex flex-col gap-1">
        <p className="text-quaternary text-xs font-semibold uppercase tracking-wide">Billing</p>
        <p className="text-secondary text-sm">{`Billed ${cadenceFrequency(subscription.cadence)}`}</p>
        {/* A membership ending at period end never reaches the scheduled
            switch — announcing it above "Access ends" would promise a change
            that will not happen. */}
        {subscription.pendingIntervalAt !== null && periodEndMeaning !== 'endsAccess' && (
          <p className="text-tertiary text-sm">
            {/* Verb first: a multi-unit cadence ("every 2 weeks") has no
                adverb to sit in front of "billing". A cadence that matches
                the current one names no change the member can see — quoting
                it would hide a price move behind "switches to monthly" —
                so it falls back to the bare date alongside the unreadable
                case. */}
            {subscription.pendingCadence === null || sameCadence(subscription.pendingCadence, subscription.cadence)
              ? 'Switches on '
              : `Switches to billing ${cadenceFrequency(subscription.pendingCadence)} on `}
            {formatPeriodEnd(subscription.pendingIntervalAt)}
          </p>
        )}
        {currentPeriodEnd !== null && periodEndMeaning !== null && (
          <p className="text-tertiary text-sm">
            {periodEndMeaning === 'endsAccess' ? 'Access ends ' : 'Next charge '}
            {formatPeriodEnd(currentPeriodEnd)}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <p className="text-quaternary text-xs font-semibold uppercase tracking-wide">Payment account</p>
        {paymentMethod === null ? (
          <p className="text-tertiary text-sm">No payment account on file.</p>
        ) : (
          <p className="text-secondary text-sm">
            {paymentMethod.institutionName ?? 'Bank account'}
            {paymentMethod.mask !== null && ` ••${paymentMethod.mask}`}
          </p>
        )}
        {subscription.isEntitled && onSwitchAccount !== undefined && (
          <div>
            <Button size="md" color="link-gray" onClick={onSwitchAccount}>
              Switch payment account
            </Button>
          </div>
        )}
      </div>

      {/* A lapsed household can subscribe again; a live one manages through
          the portal entry points, with the payment-account switch above. */}
      {subscription.isEntitled
        ? manageMembership
        : onSubscribe !== undefined && (
            <div>
              <Button size="md" onClick={onSubscribe}>
                Start subscription
              </Button>
            </div>
          )}
    </div>
  );
};

BillingSettingsBase.displayName = 'BillingSettingsBase';

export default BillingSettingsBase;
